import { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { btnPrimary, Field, inputCls, PageHead } from "./ui";

const CategoryDialog = ({ cat, onClose, onSaved }) => {
  const [f, setF] = useState({ name: cat?.name || "", description: cat?.description || "", image: cat?.image || "" });
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    try { setF({ ...f, image: (await api.post("/admin/uploads", fd)).data.url }); } catch (e) { toast.error(apiError(e)); }
  };
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      cat?.slug ? await api.patch(`/admin/categories/${cat.slug}`, f) : await api.post("/admin/categories", f);
      toast.success("Categoria salvata"); onSaved();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#F5F0E8] rounded-none" data-testid="category-dialog">
        <DialogTitle className="font-serif text-2xl text-[#39251D]">{cat?.slug ? "Modifica categoria" : "Nuova categoria"}</DialogTitle>
        <form onSubmit={save} className="space-y-4">
          <Field label="Nome"><input data-testid="category-name-input" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inputCls} /></Field>
          <Field label="Descrizione"><textarea data-testid="category-description-input" rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className={inputCls} /></Field>
          <Field label="Immagine">
            <div className="flex items-center gap-3">
              {f.image && <img src={f.image} alt="" className="w-16 h-20 object-cover" />}
              <input data-testid="category-image-input" type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} className="text-xs" />
            </div>
          </Field>
          <button data-testid="category-save" disabled={busy} className={`${btnPrimary} w-full`}>Salva</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default function AdminCategoryManager() {
  const [cats, setCats] = useState([]);
  const [editing, setEditing] = useState(null);
  const { isOwner } = useAuth();
  const load = () => api.get("/admin/categories").then((r) => setCats(r.data));
  useEffect(() => { load(); }, []);

  const move = async (i, d) => {
    const next = [...cats]; const j = i + d;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]]; setCats(next);
    await api.post("/admin/categories/reorder", { slugs: next.map((c) => c.slug) }).catch((e) => toast.error(apiError(e)));
  };
  const toggleHidden = async (c) => {
    try { await api.patch(`/admin/categories/${c.slug}`, { hidden: !c.hidden }); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (c) => {
    if (!window.confirm(`Eliminare la categoria "${c.name}"?`)) return;
    try { await api.delete(`/admin/categories/${c.slug}`); toast.success("Categoria eliminata"); load(); } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="admin-categories">
      <PageHead title="Categorie" subtitle="Ordina, nascondi o modifica. Le categorie con prodotti non possono essere eliminate.">
        <button data-testid="category-new-button" onClick={() => setEditing({})} className={btnPrimary}><Plus className="w-4 h-4" /> Nuova</button>
      </PageHead>
      <div className="space-y-2">
        {cats.map((c, i) => (
          <div key={c.slug} data-testid={`admin-category-${c.slug}`} className={`flex items-center gap-3 bg-white/60 border border-[#D5B98C]/50 p-3 ${c.hidden ? "opacity-60" : ""}`}>
            <div className="flex flex-col">
              <button data-testid={`category-up-${c.slug}`} onClick={() => move(i, -1)} className="p-1 hover:text-[#956842]"><ArrowUp className="w-4 h-4" /></button>
              <button data-testid={`category-down-${c.slug}`} onClick={() => move(i, 1)} className="p-1 hover:text-[#956842]"><ArrowDown className="w-4 h-4" /></button>
            </div>
            {c.image ? <img src={c.image} alt="" className="w-12 h-14 object-cover" /> : <div className="w-12 h-14 bg-[#E9DFCE]" />}
            <div className="flex-1 min-w-0">
              <div className="font-serif text-xl text-[#39251D]">{c.name}</div>
              <div className="text-xs text-[#956842]">{c.product_count} prodotti{c.hidden ? " · nascosta" : ""}</div>
            </div>
            <label className="flex items-center gap-2 text-xs"><span className="hidden sm:inline">Visibile</span><Switch data-testid={`category-visible-${c.slug}`} checked={!c.hidden} onCheckedChange={() => toggleHidden(c)} /></label>
            <button data-testid={`category-edit-${c.slug}`} onClick={() => setEditing(c)} className="p-2 hover:text-[#956842]"><Pencil className="w-4 h-4" /></button>
            {isOwner && <button data-testid={`category-delete-${c.slug}`} onClick={() => remove(c)} className="p-2 hover:text-[#8a2f22]"><Trash2 className="w-4 h-4" /></button>}
          </div>
        ))}
      </div>
      {editing && <CategoryDialog cat={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}
